import { useEffect, useState, useCallback } from "react";
import { Search, TrendingUp, TrendingDown, Minus, Package, AlertTriangle, Tag, Pencil, X, Plus, Trash2, ChevronUp, ChevronDown, Download, Settings as SettingsIcon, GitMerge, Wallet, PackagePlus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api, { formatApiError } from "@/lib/api";
import { formatDateShort } from "@/lib/format";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useShiftSelection } from "@/lib/useShiftSelection";
import { SortHeader } from "@/components/products/SortHeader";
import { ModeTab } from "@/components/products/ModeTab";
import { MultiMonthSelector } from "@/components/products/MultiMonthSelector";
import { AddProductManualDialog } from "@/components/products/AddProductManualDialog";

const AXIS_TICK = { fontSize: 10, fill: "#8a8a86" };
const TOOLTIP_STYLE = { fontSize: 11, borderRadius: 6 };
const LINE_DOT = { r: 3 };

function formatMoney(value, currency = "EUR") {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency, maximumFractionDigits: 4, useGrouping: "always" }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

export default function Products() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const userThreshold = user?.price_alert_threshold ?? 5;
  const [items, setItems] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("change");
  const [filterCategory, setFilterCategory] = useState("all"); // all | <name> | none
  const [threshold, setThreshold] = useState(userThreshold);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);
  // Inline price edit state: { invoice_id, line_index, name, unit_price, qty }
  const [editingLine, setEditingLine] = useState(null);
  // Tariff (list price) editor: { name, price, currency, notes, existing: bool }
  const [tariffModal, setTariffModal] = useState(null);
  // Rename product dialog: { old_name, new_name }
  const [renameModal, setRenameModal] = useState(null);
  // Manual add-product dialog
  const [addProductOpen, setAddProductOpen] = useState(false);
  // Manage-subcategories dialog
  const [manageOpen, setManageOpen] = useState(false);
  const [newSubcat, setNewSubcat] = useState({ name: "", label_es: "", color: "#2C4C3B" });
  // Top-N spend mode — supports single month, multi-month, YTD, full year
  const [topMonthly, setTopMonthly] = useState(false);
  const [topMode, setTopMode] = useState("month"); // "month" | "multi" | "ytd" | "year"
  const [topMonth, setTopMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [topMonths, setTopMonths] = useState(() => {
    const d = new Date();
    return [`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`];
  });
  const [topYear, setTopYear] = useState(() => new Date().getFullYear());

  // Hook items must have `id` for the shift-selection hook — use product name as id.
  // Note: visibleItems is computed below from `items` + filterCategory; we use
  // a temporary placeholder here and recompute selectableItems further down.
  const { selectedIds, isSelected, toggle, toggleAll, allSelected, clear } = useShiftSelection();

  // Keep the local threshold in sync if the user updates it in Settings.
  useEffect(() => { setThreshold(userThreshold); }, [userThreshold]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, threshold };
      if (q) params.q = q;
      if (topMonthly) {
        if (topMode === "month") {
          params.month = topMonth;
        } else if (topMode === "multi") {
          if (topMonths.length === 0) {
            // No months selected → behave like a single current month
            params.month = topMonth;
          } else {
            params.months = topMonths.join(",");
          }
        } else if (topMode === "ytd") {
          params.year = topYear;
          params.ytd = true;
        } else if (topMode === "year") {
          params.year = topYear;
        }
        params.top = 50;
        params.sort = "monthly_spend";
      }
      const [prods, subs] = await Promise.all([
        api.get("/products", { params }),
        api.get("/product-subcategories"),
      ]);
      setItems(prods.data);
      setSubcategories(subs.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q, sort, threshold, topMonthly, topMode, topMonth, topMonths, topYear]);

  useEffect(() => {
    const timer = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, q]);

  // Deep-link desde Dashboard PriceAlertsBanner: `/productos?q=<name>&expand=1`
  // Pre-rellena la búsqueda y, cuando llegan los items, auto-expande el producto
  // y hace scroll suave hasta él. Se ejecuta sólo una vez al montar.
  const [pendingExpand, setPendingExpand] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const qParam = sp.get("q");
    const expandParam = sp.get("expand");
    if (qParam) setQ(qParam);
    if (expandParam === "1" && qParam) setPendingExpand(qParam.trim().toLowerCase());
    if (qParam || expandParam) {
      // Limpieza de la URL para que refrescar no vuelva a disparar el auto-expand.
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!pendingExpand || loading) return;
    const match = items.find((p) => (p.name || "").toLowerCase() === pendingExpand);
    if (match) {
      setExpanded(match.name);
      // Scroll a la fila expandida después de que Recharts pinte.
      setTimeout(() => {
        const testId = `product-${match.name.replace(/\s+/g, "-")}`;
        const el = document.querySelector(`[data-testid="${testId}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
      setPendingExpand(null);
    }
  }, [pendingExpand, items, loading]);

  const foodCostCategories = subcategories;
  const catLookup = Object.fromEntries(subcategories.map((c) => [c.name, c]));
  const visibleItems = items.filter((p) => {
    if (filterCategory === "all") return true;
    if (filterCategory === "none") return !p.category;
    return p.category === filterCategory;
  });
  const selectableItems = visibleItems.map((p) => ({ ...p, id: p.name }));
  const allSelectedOnPage = allSelected(selectableItems);
  const alertCount = visibleItems.filter((p) => p.alert).length;

  // ---- Subcategory CRUD ----
  const addSubcat = async () => {
    const name = newSubcat.name.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post("/product-subcategories", {
        name,
        label_es: newSubcat.label_es || name,
        label_en: newSubcat.label_es || name,
        color: newSubcat.color,
      });
      toast.success(lang === "es" ? "Subcategoría añadida" : "Subcategory added");
      setNewSubcat({ name: "", label_es: "", color: "#2C4C3B" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeSubcat = async (name) => {
    if (!window.confirm(lang === "es" ? `¿Borrar la subcategoría "${name}"? Los productos etiquetados perderán la etiqueta.` : `Delete "${name}"? Tagged products will lose the tag.`)) return;
    setSaving(true);
    try {
      await api.delete(`/product-subcategories/${encodeURIComponent(name)}`);
      toast.success(lang === "es" ? "Subcategoría borrada" : "Subcategory deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // ---- CSV export of the visible products table ----
  const exportCSV = () => {
    if (visibleItems.length === 0) {
      toast.info(lang === "es" ? "No hay productos para exportar." : "No products to export.");
      return;
    }
    const headers = lang === "es"
      ? ["Producto", "Veces vista", "Subcategoría", "Primer precio", "Último precio", "% cambio", "Precio mínimo", "Precio máximo", "Moneda", "Proveedores"]
      : ["Product", "Times seen", "Subcategory", "First price", "Last price", "% change", "Min price", "Max price", "Currency", "Vendors"];
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v);
      // Quote whenever value contains delimiter, quote or newline
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = visibleItems.map((p) => {
      const cat = p.category ? (catLookup[p.category]?.label_es || p.category) : "";
      return [
        p.name,
        p.occurrences,
        cat,
        p.first_price ?? "",
        p.last_price ?? "",
        p.change_pct == null ? "" : p.change_pct.toFixed(2),
        p.min_price ?? "",
        p.max_price ?? "",
        p.currency || "",
        (p.vendors || []).join(" | "),
      ].map(esc).join(",");
    });
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    // Add BOM so Excel opens it with the right encoding
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().slice(0, 10);
    a.download = `productos_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(
      lang === "es"
        ? `Exportados ${visibleItems.length} productos`
        : `Exported ${visibleItems.length} products`,
    );
  };

  const exportXlsx = async () => {
    // Build the same params we sent to /api/products
    const params = { sort: "monthly_spend", threshold, top: 50 };
    if (q) params.q = q;
    if (topMode === "month") params.month = topMonth;
    else if (topMode === "multi") {
      if (topMonths.length === 0) params.month = topMonth;
      else params.months = topMonths.join(",");
    } else if (topMode === "ytd") { params.year = topYear; params.ytd = true; }
    else if (topMode === "year") { params.year = topYear; }
    try {
      const resp = await api.get("/products/export/xlsx", {
        params, responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const periodSlug =
        topMode === "month" ? topMonth :
        topMode === "multi" ? (params.months || topMonth) :
        topMode === "ytd" ? `ytd-${topYear}` :
        `year-${topYear}`;
      a.download = `top-productos-${periodSlug}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(lang === "es" ? "Excel descargado" : "Excel downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---- Bulk actions ----
  const bulkCategorize = async (catName) => {
    if (selectedIds.size === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post("/products/bulk-categorize", {
        product_names: Array.from(selectedIds),
        category: catName,
      });
      toast.success(
        catName == null
          ? (lang === "es" ? `Categoría borrada de ${data.cleared} productos` : `Cleared category from ${data.cleared} products`)
          : (lang === "es" ? `${data.updated} productos asignados a "${catName}"` : `${data.updated} products tagged "${catName}"`),
      );
      clear();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // ---- Merge / unify products ----
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");
  const openMerge = () => {
    if (selectedIds.size < 2) {
      toast.error(lang === "es"
        ? "Selecciona al menos 2 productos para unificar"
        : "Select at least 2 products to merge");
      return;
    }
    // Default to first selected name as the canonical target
    setMergeTarget(Array.from(selectedIds)[0]);
    setMergeOpen(true);
  };
  const doMerge = async () => {
    const tgt = (mergeTarget || "").trim().toLowerCase();
    if (!tgt) {
      toast.error(lang === "es" ? "Indica el nombre destino" : "Pick a target name");
      return;
    }
    const sources = Array.from(selectedIds).filter((n) => n !== tgt);
    if (sources.length === 0) {
      toast.error(lang === "es"
        ? "Necesitas al menos 1 producto origen distinto del destino"
        : "Need at least 1 source different from the target");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/products/merge", {
        source_names: sources,
        target_name: tgt,
      });
      toast.success(
        lang === "es"
          ? `${data.line_items_renamed} líneas renombradas en ${data.invoices_touched} factura(s) → "${tgt}"`
          : `Renamed ${data.line_items_renamed} lines in ${data.invoices_touched} invoice(s) → "${tgt}"`,
      );
      setMergeOpen(false);
      clear();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // ---- Inline line-item edit ----
  const openLineEdit = (productName, historyPoint) => {
    if (historyPoint.line_index == null || !historyPoint.invoice_id) {
      toast.error(lang === "es" ? "No se puede editar esta línea." : "Cannot edit this line.");
      return;
    }
    setEditingLine({
      product_name: productName,
      invoice_id: historyPoint.invoice_id,
      line_index: historyPoint.line_index,
      name: productName,
      qty: historyPoint.qty,
      unit_price: historyPoint.unit_price,
    });
  };

  const submitLineEdit = async () => {
    if (!editingLine) return;
    setSaving(true);
    try {
      const payload = {
        name: editingLine.name,
        qty: Number(editingLine.qty),
        unit_price: Number(editingLine.unit_price),
      };
      await api.patch(
        `/invoices/${editingLine.invoice_id}/line-items/${editingLine.line_index}`,
        payload,
      );
      toast.success(lang === "es" ? "Precio actualizado" : "Price updated");
      setEditingLine(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // ---- Tariff (list price) actions ----
  const openTariffEditor = (product) => {
    setTariffModal({
      name: product.name,
      price: product.tariff_price != null ? String(product.tariff_price) : "",
      currency: product.tariff_currency || product.currency || user?.base_currency || "EUR",
      notes: "",
      existing: product.tariff_price != null,
    });
  };
  const submitTariff = async () => {
    if (!tariffModal) return;
    const price = parseFloat(tariffModal.price);
    if (isNaN(price) || price < 0) {
      toast.error(lang === "es" ? "Precio inválido" : "Invalid price");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/products/tariff`, {
        price,
        currency: (tariffModal.currency || "EUR").toUpperCase(),
        notes: tariffModal.notes || null,
      }, { params: { product_name: tariffModal.name } });
      toast.success(lang === "es" ? "Tarifa guardada" : "Tariff saved");
      setTariffModal(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  const clearTariff = async () => {
    if (!tariffModal) return;
    setSaving(true);
    try {
      await api.delete(`/products/tariff`, { params: { product_name: tariffModal.name } });
      toast.success(lang === "es" ? "Tarifa eliminada" : "Tariff cleared");
      setTariffModal(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // ---- Rename a single product (thin wrapper over /products/merge) ----
  const openRename = (product) => {
    setRenameModal({ old_name: product.name, new_name: product.name });
  };
  const submitRename = async () => {
    if (!renameModal) return;
    const newName = (renameModal.new_name || "").trim().toLowerCase();
    if (!newName || newName === renameModal.old_name) {
      toast.error(lang === "es" ? "Introduce un nombre distinto" : "Pick a different name");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/products/merge", {
        source_names: [renameModal.old_name],
        target_name: newName,
      });
      toast.success(lang === "es"
        ? `Renombrado — ${data.line_items_renamed} línea(s) actualizada(s)`
        : `Renamed — ${data.line_items_renamed} line(s) updated`);
      setRenameModal(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
            {t("products")}
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
            {t("productsTitle")}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2">
            {lang === "es"
              ? "Análisis de precios sobre las facturas de categoría Food Cost. Selecciona varios productos (Shift+clic) para clasificarlos por tipo (vinos, carnes, lácteos…)."
              : "Price analysis on Food Cost invoices. Select multiple products (Shift+click) to tag them by type."}
          </p>
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold mt-2 px-2 py-0.5 rounded bg-[var(--status-positive-bg)] text-[var(--status-positive)]" data-testid="food-cost-only-badge">
            {lang === "es" ? "Solo Food Cost" : "Food Cost only"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setAddProductOpen(true)}
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            data-testid="products-add-manual-btn"
          >
            <PackagePlus size={14} className="mr-1.5" />
            {lang === "es" ? "Nuevo producto" : "New product"}
          </Button>
          <Button
            variant="outline"
            onClick={exportCSV}
            disabled={visibleItems.length === 0}
            data-testid="products-export-csv"
          >
            <Download size={14} className="mr-1.5" />
            {lang === "es" ? "Exportar CSV" : "Export CSV"}
          </Button>
          <Button
            variant="outline"
            onClick={exportXlsx}
            disabled={!topMonthly}
            data-testid="products-export-xlsx"
            title={topMonthly
              ? (lang === "es" ? "Descarga el Top 50 en Excel" : "Download Top 50 as Excel")
              : (lang === "es" ? "Activa 'Top 50 gastos por producto' primero" : "Enable 'Top 50 product spend' first")}
          >
            <Download size={14} className="mr-1.5" />
            {lang === "es" ? "Top 50 · Excel" : "Top 50 · Excel"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setManageOpen(true)}
            data-testid="products-manage-subcategories"
          >
            <SettingsIcon size={14} className="mr-1.5" />
            {lang === "es" ? "Gestionar subcategorías" : "Manage subcategories"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input
            placeholder={lang === "es" ? "Buscar producto..." : "Search product..."}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="products-search"
            className="pl-9 h-9"
          />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="products-sort" className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="change">{t("sortByChange")}</SelectItem>
            <SelectItem value="name">{lang === "es" ? "Nombre A-Z" : "Name A-Z"}</SelectItem>
            <SelectItem value="category">{lang === "es" ? "Subcategoría A-Z" : "Subcategory A-Z"}</SelectItem>
            <SelectItem value="occurrences">{t("sortByOccurrences")}</SelectItem>
            <SelectItem value="last_price">{t("sortByLastPrice")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger data-testid="products-filter-category" className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {lang === "es" ? "Todas las subcategorías" : "All subcategories"}
            </SelectItem>
            <SelectItem value="none">
              {lang === "es" ? "Sin subcategoría" : "Untagged"}
            </SelectItem>
            {subcategories.map((c) => (
              <SelectItem key={c.name} value={c.name}>
                {lang === "es" ? c.label_es : c.label_en}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative">
          <AlertTriangle size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input
            type="number"
            step="0.5"
            min="0"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value === "" ? 0 : parseFloat(e.target.value))}
            data-testid="products-threshold"
            className="pl-9 h-9 font-mono-num"
            title={t("priceAlertHelp")}
            placeholder={t("priceAlertThreshold")}
          />
        </div>
      </div>

      {/* Top-50 spend mode toolbar — single month / multi-month / YTD / full year */}
      <div
        className={`flex flex-col gap-2 px-4 py-3 rounded-lg border ${
          topMonthly
            ? "bg-[var(--brand-accent)]/10 border-[var(--brand-accent)]"
            : "bg-[var(--surface)] border-[var(--border-color)]"
        }`}
        data-testid="products-top-monthly-bar"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={topMonthly}
              onChange={(e) => setTopMonthly(e.target.checked)}
              data-testid="products-top-monthly-toggle"
              className="rounded border-[var(--border-color)]"
            />
            <span className="text-sm font-medium">
              {lang === "es" ? "Top 50 gastos por producto" : "Top 50 product spend"}
            </span>
          </label>
          {topMonthly && (
            <>
              <div className="inline-flex rounded-md border border-[var(--border-color)] overflow-hidden text-xs">
                <ModeTab value="month" current={topMode} onClick={setTopMode}>
                  {lang === "es" ? "Mes" : "Month"}
                </ModeTab>
                <ModeTab value="multi" current={topMode} onClick={setTopMode}>
                  {lang === "es" ? "Varios meses" : "Multi-month"}
                </ModeTab>
                <ModeTab value="ytd" current={topMode} onClick={setTopMode}>
                  {lang === "es" ? "YTD" : "YTD"}
                </ModeTab>
                <ModeTab value="year" current={topMode} onClick={setTopMode}>
                  {lang === "es" ? "Año completo" : "Full year"}
                </ModeTab>
              </div>
              {topMode === "month" && (
                <Input
                  type="month"
                  value={topMonth}
                  onChange={(e) => setTopMonth(e.target.value)}
                  data-testid="products-top-monthly-month"
                  className="h-8 w-44 font-mono-num"
                />
              )}
              {(topMode === "ytd" || topMode === "year") && (
                <Input
                  type="number"
                  min="2000"
                  max="2100"
                  value={topYear}
                  onChange={(e) => setTopYear(parseInt(e.target.value, 10) || new Date().getFullYear())}
                  data-testid="products-top-year"
                  className="h-8 w-24 font-mono-num"
                />
              )}
              <span className="text-xs text-[var(--text-muted)] ml-auto">
                {topMode === "month" && (lang === "es" ? "Mes único" : "Single month")}
                {topMode === "multi" && (lang === "es"
                  ? `${topMonths.length} mes(es) seleccionado(s)`
                  : `${topMonths.length} month(s) selected`)}
                {topMode === "ytd" && (lang === "es"
                  ? `Acumulado ${topYear} (Ene → mes actual)`
                  : `YTD ${topYear} (Jan → current month)`)}
                {topMode === "year" && (lang === "es"
                  ? `Total año ${topYear} (Ene → Dic)`
                  : `Full year ${topYear} (Jan → Dec)`)}
              </span>
            </>
          )}
        </div>
        {topMonthly && topMode === "multi" && (
          <MultiMonthSelector value={topMonths} onChange={setTopMonths} lang={lang} />
        )}
      </div>

      {alertCount > 0 && (
        <div
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--status-warning-bg)] text-[var(--status-warning)] border border-[var(--status-warning)]/30"
          data-testid="products-alert-summary"
        >
          <AlertTriangle size={14} />
          <p className="text-sm">
            <span className="font-medium">{alertCount}</span>{" "}
            {lang === "es"
              ? `producto${alertCount === 1 ? "" : "s"} con cambios superiores al ${threshold}%`
              : `product${alertCount === 1 ? "" : "s"} changed more than ${threshold}%`}
          </p>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg bg-[var(--surface)] border border-[var(--accent)]"
          data-testid="products-bulk-bar"
        >
          <span className="text-xs text-[var(--text-secondary)]">
            {selectedIds.size} {lang === "es" ? "productos seleccionados" : "products selected"}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={saving || selectedIds.size < 2}
              onClick={openMerge}
              data-testid="products-merge-btn"
            >
              <GitMerge size={12} className="mr-1" />
              {lang === "es" ? "Unificar" : "Merge"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" disabled={saving} data-testid="products-bulk-categorize">
                  <Tag size={12} className="mr-1" />
                  {lang === "es" ? "Cambiar categoría" : "Change category"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
                {foodCostCategories.length === 0 && (
                  <DropdownMenuItem disabled>
                    {lang === "es" ? "Sin categorías Food Cost" : "No food-cost categories"}
                  </DropdownMenuItem>
                )}
                {foodCostCategories.map((c) => (
                  <DropdownMenuItem
                    key={c.name}
                    onClick={() => bulkCategorize(c.name)}
                    data-testid={`products-cat-item-${c.name}`}
                  >
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-2"
                      style={{ backgroundColor: c.color }}
                    />
                    {lang === "es" ? c.label_es : c.label_en}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem
                  onClick={() => bulkCategorize(null)}
                  data-testid="products-cat-clear"
                  className="border-t border-[var(--border-color)] mt-1 pt-2"
                >
                  <X size={12} className="mr-2" />
                  {lang === "es" ? "Quitar categoría" : "Clear category"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={clear}
              className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"
              data-testid="products-bulk-clear"
              title={lang === "es" ? "Limpiar selección" : "Clear selection"}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center text-sm text-[var(--text-muted)] py-12">...</div>
      ) : visibleItems.length === 0 ? (
        <div className="border border-dashed border-[var(--border-color)] rounded-lg py-16 text-center" data-testid="products-empty">
          <Package size={32} className="mx-auto mb-3 text-[var(--text-muted)]" strokeWidth={1.5} />
          <p className="text-sm text-[var(--text-secondary)]">
            {filterCategory === "all" ? t("noProducts") : (lang === "es" ? "Sin productos en esta subcategoría." : "No products in this subcategory.")}
          </p>
        </div>
      ) : (
        <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="products-table">
            <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--surface-hover)]/40">
              <tr className="border-b border-[var(--border-color)]">
                <th className="text-left py-2.5 px-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelectedOnPage}
                    onChange={() => toggleAll(selectableItems)}
                    data-testid="products-select-all"
                    className="rounded border-[var(--border-color)]"
                  />
                </th>
                <SortHeader sortKey="name" current={sort} onChange={setSort} testid="th-name">
                  {lang === "es" ? "Producto" : "Product"}
                </SortHeader>
                <SortHeader sortKey="occurrences" current={sort} onChange={setSort} testid="th-occ" align="right">
                  {lang === "es" ? "Veces vista" : "Times seen"}
                </SortHeader>
                <SortHeader sortKey="category" current={sort} onChange={setSort} testid="th-cat">
                  {lang === "es" ? "Subcategoría" : "Subcategory"}
                </SortHeader>
                <SortHeader sortKey="first_price" current={sort} onChange={setSort} testid="th-first" align="right">
                  {lang === "es" ? "Primer precio" : "First price"}
                </SortHeader>
                <SortHeader sortKey="last_price" current={sort} onChange={setSort} testid="th-last" align="right">
                  {lang === "es" ? "Último precio" : "Last price"}
                </SortHeader>
                <th className="text-right py-2.5 px-3 font-medium text-[10px] uppercase tracking-wider text-[var(--text-muted)] whitespace-nowrap" title={lang === "es" ? "Fecha del último precio conocido" : "Date of last known price"}>
                  {lang === "es" ? "Última act." : "Last update"}
                </th>
                <SortHeader sortKey="change" current={sort} onChange={setSort} testid="th-pct" align="right">
                  %
                </SortHeader>
                <th className="text-right py-2.5 px-3 font-medium text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                  {lang === "es" ? "Tarifa" : "Tariff"}
                </th>
                {topMonthly && (
                  <SortHeader sortKey="monthly_spend" current={sort} onChange={setSort} testid="th-monthly" align="right">
                    {lang === "es" ? "Gasto del mes" : "Monthly spend"}
                  </SortHeader>
                )}
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {selectableItems.map((p, idx) => (
                <ProductRow
                  key={p.name}
                  product={p}
                  idx={idx}
                  items={selectableItems}
                  checked={isSelected(p.name)}
                  onToggle={(e) => toggle(p.name, idx, selectableItems, e.shiftKey)}
                  expanded={expanded === p.name}
                  onExpand={() => setExpanded(expanded === p.name ? null : p.name)}
                  category={p.category ? catLookup[p.category] : null}
                  subcategories={subcategories}
                  showMonthlySpend={topMonthly}
                  onChangeCategory={async (newCat) => {
                    try {
                      await api.post("/products/bulk-categorize", {
                        product_names: [p.name],
                        category: newCat,
                      });
                      toast.success(
                        newCat == null
                          ? (lang === "es" ? "Subcategoría eliminada" : "Subcategory removed")
                          : (lang === "es" ? `Asignado a "${newCat}"` : `Tagged "${newCat}"`),
                      );
                      await load();
                    } catch (err) {
                      toast.error(formatApiError(err));
                    }
                  }}
                  lang={lang}
                  t={t}
                  onEditLine={(hp) => openLineEdit(p.name, hp)}
                  onOpenTariff={() => openTariffEditor(p)}
                  onRename={() => openRename(p)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit line-item modal */}
      <Dialog open={!!editingLine} onOpenChange={(o) => { if (!o && !saving) setEditingLine(null); }}>
        <DialogContent data-testid="product-line-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              {lang === "es" ? "Corregir precio del producto" : "Fix product price"}
            </DialogTitle>
          </DialogHeader>
          {editingLine && (
            <div className="space-y-4">
              <p className="text-xs text-[var(--text-muted)]">
                {lang === "es"
                  ? "Edita los datos extraídos por la IA. Se actualizará esta línea en la factura original."
                  : "Edit the OCR-extracted values. This will update the line in the original invoice."}
              </p>
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                  {lang === "es" ? "Nombre del producto" : "Product name"}
                </label>
                <Input
                  data-testid="product-line-edit-name"
                  value={editingLine.name}
                  onChange={(e) => setEditingLine({ ...editingLine, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                    {lang === "es" ? "Cantidad" : "Quantity"}
                  </label>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    data-testid="product-line-edit-qty"
                    value={editingLine.qty ?? ""}
                    onChange={(e) => setEditingLine({ ...editingLine, qty: e.target.value })}
                    className="font-mono-num"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                    {lang === "es" ? "Precio unitario" : "Unit price"}
                  </label>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    data-testid="product-line-edit-price"
                    value={editingLine.unit_price ?? ""}
                    onChange={(e) => setEditingLine({ ...editingLine, unit_price: e.target.value })}
                    className="font-mono-num"
                  />
                </div>
              </div>
              <p className="text-[10px] text-[var(--text-muted)]">
                {lang === "es"
                  ? "El total de la línea se recalculará automáticamente como cantidad × precio."
                  : "Line total will be auto-recomputed as qty × unit_price."}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditingLine(null)} disabled={saving} data-testid="product-line-edit-cancel">
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button type="button" onClick={submitLineEdit} disabled={saving} data-testid="product-line-edit-save">
              {saving
                ? (lang === "es" ? "Guardando…" : "Saving…")
                : (lang === "es" ? "Guardar" : "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Manage subcategories dialog */}
      <Dialog open={manageOpen} onOpenChange={(o) => { if (!o) setManageOpen(false); }}>
        <DialogContent data-testid="manage-subcategories-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {lang === "es" ? "Subcategorías de productos" : "Product subcategories"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[var(--text-muted)]">
              {lang === "es"
                ? "Estas categorías se usan únicamente para clasificar productos en esta tabla. No aparecen en /categorías."
                : "Used only to tag products on this page. They don't show up on /categories."}
            </p>
            <div className="max-h-56 overflow-y-auto space-y-1.5">
              {subcategories.map((sc) => (
                <div
                  key={sc.name}
                  className="flex items-center justify-between gap-2 px-3 py-2 bg-[var(--surface-hover)]/40 rounded border border-[var(--border-color)]"
                  data-testid={`subcat-row-${sc.name}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: sc.color }} />
                    <span className="text-sm truncate">
                      {lang === "es" ? sc.label_es : sc.label_en}
                    </span>
                    <span className="text-[10px] font-mono text-[var(--text-muted)] truncate">{sc.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSubcat(sc.name)}
                    className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--status-negative)]"
                    disabled={saving}
                    data-testid={`subcat-delete-${sc.name}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div className="border-t border-[var(--border-color)] pt-3 space-y-2">
              <p className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                {lang === "es" ? "Añadir nueva" : "Add new"}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder={lang === "es" ? "Nombre (ej. especias)" : "Name (e.g. spices)"}
                  value={newSubcat.name}
                  onChange={(e) => setNewSubcat({ ...newSubcat, name: e.target.value })}
                  data-testid="subcat-new-name"
                />
                <Input
                  placeholder={lang === "es" ? "Etiqueta visible" : "Display label"}
                  value={newSubcat.label_es}
                  onChange={(e) => setNewSubcat({ ...newSubcat, label_es: e.target.value })}
                  data-testid="subcat-new-label"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newSubcat.color}
                  onChange={(e) => setNewSubcat({ ...newSubcat, color: e.target.value })}
                  className="w-9 h-9 rounded border border-[var(--border-color)] cursor-pointer"
                  data-testid="subcat-new-color"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={addSubcat}
                  disabled={saving || !newSubcat.name.trim()}
                  data-testid="subcat-new-add"
                >
                  <Plus size={12} className="mr-1" />
                  {lang === "es" ? "Añadir" : "Add"}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setManageOpen(false)} data-testid="manage-subcategories-close">
              {lang === "es" ? "Cerrar" : "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mergeOpen} onOpenChange={(o) => !o && setMergeOpen(false)}>
        <DialogContent className="max-w-md" data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle>
              {lang === "es" ? "Unificar productos" : "Merge products"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[var(--text-secondary)]">
              {lang === "es" ? (
                <>Vas a unificar <b>{selectedIds.size}</b> productos en un único nombre. Todas las facturas pasadas se renombrarán automáticamente. Esta acción no se puede deshacer.</>
              ) : (
                <>You&apos;ll merge <b>{selectedIds.size}</b> products into one. All past invoices will be renamed automatically. This cannot be undone.</>
              )}
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider">
                {lang === "es" ? "Productos seleccionados" : "Selected products"}
              </Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {Array.from(selectedIds).map((n) => (
                  <span key={n}
                    className={`text-xs px-2 py-1 rounded ${n === mergeTarget
                      ? "bg-[var(--primary)] text-white"
                      : "bg-[var(--surface-hover)] text-[var(--text-secondary)]"}`}>
                    {n}
                  </span>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider" htmlFor="merge-target">
                {lang === "es" ? "Nombre final (destino)" : "Final name (target)"}
              </Label>
              <Input
                id="merge-target"
                value={mergeTarget}
                onChange={(e) => setMergeTarget(e.target.value.toLowerCase())}
                placeholder={lang === "es" ? "ej. tomate" : "e.g. tomato"}
                list="merge-target-options"
                data-testid="merge-target-input"
              />
              <datalist id="merge-target-options">
                {Array.from(selectedIds).map((n) => <option key={n} value={n} />)}
              </datalist>
              <p className="text-[10px] text-[var(--text-muted)]">
                {lang === "es"
                  ? "Puedes elegir uno de los seleccionados o escribir un nombre nuevo (todo en minúsculas)."
                  : "Pick one of the selected names or type a new one (lowercase)."}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)} disabled={saving}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={doMerge}
              disabled={saving || !mergeTarget.trim()}
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
              data-testid="merge-confirm"
            >
              {saving ? "…" : (lang === "es" ? "Unificar" : "Merge")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tariff editor */}
      <Dialog open={!!tariffModal} onOpenChange={(o) => { if (!o && !saving) setTariffModal(null); }}>
        <DialogContent data-testid="tariff-dialog">
          <DialogHeader>
            <DialogTitle>
              {tariffModal?.existing
                ? (lang === "es" ? `Editar tarifa · ${tariffModal.name}` : `Edit tariff · ${tariffModal.name}`)
                : (lang === "es" ? `Fijar tarifa · ${tariffModal?.name}` : `Set tariff · ${tariffModal?.name}`)}
            </DialogTitle>
          </DialogHeader>
          {tariffModal && (
            <div className="space-y-4">
              <p className="text-xs text-[var(--text-muted)]">
                {lang === "es"
                  ? "Precio de tarifa manual del proveedor. Se comparará con el último precio facturado y avisará si hay diferencia."
                  : "Manual list price from the vendor. Compared against the latest invoiced price to flag deviations."}
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Precio unitario" : "Unit price"}</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={tariffModal.price}
                    onChange={(e) => setTariffModal({ ...tariffModal, price: e.target.value })}
                    className="font-mono-num"
                    autoFocus
                    data-testid="tariff-price"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Moneda" : "Currency"}</Label>
                  <Input
                    value={tariffModal.currency}
                    onChange={(e) => setTariffModal({ ...tariffModal, currency: e.target.value.toUpperCase().slice(0, 3) })}
                    className="font-mono-num uppercase"
                    data-testid="tariff-currency"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Notas (opcional)" : "Notes (optional)"}</Label>
                <Input
                  value={tariffModal.notes}
                  onChange={(e) => setTariffModal({ ...tariffModal, notes: e.target.value })}
                  placeholder={lang === "es" ? "Ej: precio pactado 2025" : "e.g. agreed price 2025"}
                  data-testid="tariff-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {tariffModal?.existing && (
              <Button variant="outline" onClick={clearTariff} disabled={saving} className="mr-auto text-[var(--status-negative)]" data-testid="tariff-clear">
                <Trash2 size={13} className="mr-1.5" />
                {lang === "es" ? "Eliminar tarifa" : "Clear tariff"}
              </Button>
            )}
            <Button variant="outline" onClick={() => setTariffModal(null)} disabled={saving}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button onClick={submitTariff} disabled={saving} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" data-testid="tariff-save">
              {saving ? "…" : (lang === "es" ? "Guardar" : "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename product */}
      <Dialog open={!!renameModal} onOpenChange={(o) => { if (!o && !saving) setRenameModal(null); }}>
        <DialogContent data-testid="rename-product-dialog">
          <DialogHeader>
            <DialogTitle>
              {lang === "es" ? `Renombrar producto · ${renameModal?.old_name}` : `Rename product · ${renameModal?.old_name}`}
            </DialogTitle>
          </DialogHeader>
          {renameModal && (
            <div className="space-y-3">
              <p className="text-xs text-[var(--text-muted)]">
                {lang === "es"
                  ? "Se actualizarán TODAS las líneas de factura con este nombre. La subcategoría y la tarifa se mantienen."
                  : "Every invoice line-item with this name will be renamed. Subcategory and tariff are preserved."}
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Nuevo nombre" : "New name"}</Label>
                <Input
                  value={renameModal.new_name}
                  onChange={(e) => setRenameModal({ ...renameModal, new_name: e.target.value })}
                  autoFocus
                  data-testid="rename-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameModal(null)} disabled={saving}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button onClick={submitRename} disabled={saving} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" data-testid="rename-save">
              {saving ? "…" : (lang === "es" ? "Renombrar" : "Rename")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Manual add product */}
      {addProductOpen && (
        <AddProductManualDialog
          existingNames={new Set(items.map((p) => (p.name || "").toLowerCase()))}
          baseCurrency={user?.base_currency || "EUR"}
          lang={lang}
          onClose={() => setAddProductOpen(false)}
          onSaved={() => { setAddProductOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function ProductRow({ product, idx, items, checked, onToggle, expanded, onExpand, category, subcategories, showMonthlySpend, onChangeCategory, lang, t, onEditLine, onOpenTariff, onRename }) {
  const change = product.change_pct;
  const trendIcon =
    change == null ? <Minus size={14} /> : change > 0.1 ? <TrendingUp size={14} /> : change < -0.1 ? <TrendingDown size={14} /> : <Minus size={14} />;
  const trendColor =
    change == null
      ? "text-[var(--text-muted)] bg-[var(--surface-hover)]"
      : change > 0.1
      ? "text-[var(--status-negative)] bg-[var(--status-negative-bg)]"
      : change < -0.1
      ? "text-[var(--status-positive)] bg-[var(--status-positive-bg)]"
      : "text-[var(--text-muted)] bg-[var(--surface-hover)]";

  const onRowClick = (e) => {
    if (["INPUT", "BUTTON", "A", "SELECT", "TEXTAREA", "SVG", "PATH"].includes(e.target.tagName?.toUpperCase?.())) return;
    if (e.shiftKey) {
      e.preventDefault();
      window.getSelection?.()?.removeAllRanges?.();
      onToggle({ shiftKey: true });
      return;
    }
    onExpand();
  };

  return (
    <>
      <tr
        className={`border-b border-[var(--border-color)] last:border-0 hover:bg-[var(--surface-hover)] cursor-pointer ${product.alert ? "ring-1 ring-inset ring-[var(--status-warning)]/40" : ""}`}
        data-testid={`product-${product.name.replace(/\s+/g, "-")}`}
        data-alert={product.alert ? "true" : "false"}
        data-row-idx={idx}
        onClick={onRowClick}
      >
        <td className="py-2.5 px-3">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => {}}
            onClick={(e) => { e.stopPropagation(); onToggle(e); }}
            data-testid={`product-select-${idx}`}
            className="rounded border-[var(--border-color)]"
          />
        </td>
        <td className="py-2.5 px-3" data-testid={`product-name-${idx}`}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-medium capitalize truncate">{product.name}</span>
            {product.alert && (
              <span
                title={t("aboveThreshold")}
                data-testid={`product-alert-${product.name.replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[var(--status-warning-bg)] text-[var(--status-warning)] shrink-0"
              >
                <AlertTriangle size={10} />
                {t("priceAlert")}
              </span>
            )}
          </div>
          {product.vendors.length > 0 && (
            <p className="text-[10px] text-[var(--text-muted)] truncate">
              {product.vendors.slice(0, 2).join(" · ")}
              {product.vendors.length > 2 && ` +${product.vendors.length - 2}`}
            </p>
          )}
        </td>
        <td className="py-2.5 px-3 text-right font-mono-num text-sm" data-testid={`product-occ-${idx}`}>
          {product.occurrences}
        </td>
        <td className="py-2.5 px-3" data-testid={`product-cat-cell-${idx}`} onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border ${
                  category
                    ? "border-transparent"
                    : "border-dashed border-[var(--border-color)] text-[var(--text-muted)]"
                } hover:bg-[var(--surface-hover)]`}
                style={category ? { backgroundColor: `${category.color}22`, color: category.color } : undefined}
                data-testid={`product-category-badge-${idx}`}
                title={lang === "es" ? "Cambiar subcategoría" : "Change subcategory"}
              >
                <Tag size={10} />
                {category
                  ? (lang === "es" ? category.label_es : category.label_en)
                  : (lang === "es" ? "Sin etiqueta" : "Untagged")}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
              {subcategories.map((c) => (
                <DropdownMenuItem
                  key={c.name}
                  onClick={() => onChangeCategory(c.name)}
                  data-testid={`product-row-cat-${idx}-${c.name}`}
                >
                  <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: c.color }} />
                  {lang === "es" ? c.label_es : c.label_en}
                </DropdownMenuItem>
              ))}
              {category && (
                <DropdownMenuItem
                  onClick={() => onChangeCategory(null)}
                  data-testid={`product-row-cat-${idx}-clear`}
                  className="border-t border-[var(--border-color)] mt-1 pt-2"
                >
                  <X size={12} className="mr-2" />
                  {lang === "es" ? "Quitar etiqueta" : "Clear tag"}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
        <td className="py-2.5 px-3 text-right font-mono-num text-sm" data-testid={`product-first-${idx}`}>
          {formatMoney(product.first_price, product.currency)}
        </td>
        <td className="py-2.5 px-3 text-right font-mono-num text-sm" data-testid={`product-last-${idx}`}>
          {formatMoney(product.last_price, product.currency)}
        </td>
        <td className="py-2.5 px-3 text-right text-xs text-[var(--text-muted)] font-mono-num whitespace-nowrap" data-testid={`product-last-date-${idx}`}>
          {product.last_price_date || "—"}
        </td>
        <td className="py-2.5 px-3 text-right" data-testid={`product-change-${idx}`}>
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${trendColor}`}>
            {trendIcon}
            {change == null ? "—" : `${change > 0 ? "+" : ""}${change.toFixed(1)}%`}
          </span>
          {product.yearly_alert && (
            <span
              className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[var(--status-warning-bg)] text-[var(--status-warning)] whitespace-nowrap"
              title={lang === "es"
                ? `Subida ${product.yearly_change_pct?.toFixed(1)}% este año — revisa PVP + escandallo`
                : `Up ${product.yearly_change_pct?.toFixed(1)}% this year — review PVP + dish cost`}
              data-testid={`product-yearly-alert-${idx}`}
            >
              <AlertTriangle size={9} />
              +{product.yearly_change_pct?.toFixed(1)}%
            </span>
          )}
        </td>
        <td className="py-2.5 px-3 text-right" data-testid={`product-tariff-${idx}`}>
          {product.tariff_price != null ? (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenTariff(); }}
              className={`inline-flex flex-col items-end gap-0 group ${product.tariff_alert ? "text-amber-600" : "text-[var(--text-secondary)]"}`}
              title={lang === "es" ? "Editar tarifa" : "Edit tariff"}
              data-testid={`product-tariff-btn-${idx}`}
            >
              <span className="font-mono-num text-sm font-semibold">
                {formatMoney(product.tariff_price, product.tariff_currency || product.currency)}
              </span>
              {product.tariff_variance_pct != null && (
                <span className={`text-[10px] font-mono-num inline-flex items-center gap-0.5 ${product.tariff_alert ? "text-amber-600 font-semibold" : "text-[var(--text-muted)]"}`}>
                  {product.tariff_alert && <AlertTriangle size={9} />}
                  {product.tariff_variance_pct > 0 ? "+" : ""}{product.tariff_variance_pct.toFixed(1)}%
                </span>
              )}
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenTariff(); }}
              className="text-[10px] text-[var(--text-muted)] hover:text-[var(--primary)] underline decoration-dotted inline-flex items-center gap-1"
              data-testid={`product-set-tariff-${idx}`}
            >
              <Wallet size={10} />
              {lang === "es" ? "Fijar tarifa" : "Set tariff"}
            </button>
          )}
        </td>
        {showMonthlySpend && (
          <td className="py-2.5 px-3 text-right font-mono-num text-sm font-semibold" data-testid={`product-monthly-${idx}`}>
            {formatMoney(product.monthly_spend, product.currency)}
          </td>
        )}
        <td className="py-2.5 px-2 text-right">
          <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={onRename}
              className="text-[var(--text-muted)] hover:text-[var(--primary)] p-1"
              title={lang === "es" ? "Renombrar producto" : "Rename product"}
              data-testid={`product-rename-btn-${idx}`}
            >
              <Pencil size={12} />
            </button>
            {expanded ? <ChevronUp size={14} className="text-[var(--text-muted)]" /> : <ChevronDown size={14} className="text-[var(--text-muted)]" />}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[var(--surface-hover)]/40">
          <td colSpan={showMonthlySpend ? 10 : 9} className="border-t border-[var(--border-color)] p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-2">{t("priceHistory")}</p>
                <div className="h-48 bg-[var(--surface)] rounded border border-[var(--border-color)] p-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={product.history.map((h) => ({ date: h.date, price: h.unit_price }))}>
                      <XAxis dataKey="date" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                      <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={TOOLTIP_STYLE}
                        formatter={(v) => formatMoney(v, product.currency)}
                      />
                      <Line type="monotone" dataKey="price" stroke="#2C4C3B" strokeWidth={2} dot={LINE_DOT} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div className="bg-[var(--surface)] rounded border border-[var(--border-color)] px-3 py-2">
                    <p className="text-[var(--text-muted)]">{t("minPrice")}</p>
                    <p className="font-mono-num font-medium">{formatMoney(product.min_price, product.currency)}</p>
                  </div>
                  <div className="bg-[var(--surface)] rounded border border-[var(--border-color)] px-3 py-2">
                    <p className="text-[var(--text-muted)]">{t("maxPrice")}</p>
                    <p className="font-mono-num font-medium">{formatMoney(product.max_price, product.currency)}</p>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                    {lang === "es" ? "Detalle por factura (corrige precios)" : "Per-invoice detail (fix prices)"}
                  </p>
                  {(() => {
                    const rows = product.history || [];
                    const totalQty = rows.reduce((s, h) => s + (parseFloat(h.qty) || 0), 0);
                    const totalSpend = rows.reduce((s, h) => s + ((parseFloat(h.qty) || 0) * (parseFloat(h.unit_price) || 0)), 0);
                    return (
                      <p className="text-[10px] text-[var(--text-muted)] flex items-center gap-3" data-testid={`product-history-summary-${idx}`}>
                        <span>
                          {lang === "es" ? "Total" : "Total"}:{" "}
                          <span className="font-mono-num font-semibold text-[var(--text-primary)]">
                            {totalQty.toLocaleString(lang === "es" ? "es-ES" : "en-US", { maximumFractionDigits: 2, useGrouping: "always" })}
                          </span>
                          {" ud"}
                        </span>
                        <span>·</span>
                        <span>
                          {lang === "es" ? "Gasto" : "Spend"}:{" "}
                          <span className="font-mono-num font-semibold text-[var(--text-primary)]">
                            {formatMoney(totalSpend, product.currency)}
                          </span>
                        </span>
                      </p>
                    );
                  })()}
                </div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {product.history.slice().reverse().map((h, i) => (
                    <div
                      key={`${h.invoice_id || h.date}-${i}`}
                      className="flex items-center justify-between text-xs bg-[var(--surface)] border border-[var(--border-color)] rounded px-3 py-2"
                      data-testid={`product-history-row-${idx}-${i}`}
                    >
                      <div className="min-w-0">
                        <p className="font-mono-num text-[var(--text-muted)]">{formatDateShort(h.date)}</p>
                        <p className="truncate">{h.vendor || "—"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="font-mono-num font-medium">{formatMoney(h.unit_price, h.currency || product.currency)}</p>
                          <p className="text-[var(--text-muted)]">x {h.qty}</p>
                        </div>
                        {h.line_index != null && h.invoice_id && (
                          <button
                            onClick={() => onEditLine(h)}
                            className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"
                            title={lang === "es" ? "Editar precio" : "Edit price"}
                            data-testid={`product-edit-line-${idx}-${i}`}
                          >
                            <Pencil size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

